import { request, type ClientRequest } from "node:http";

import { requireEnv } from "@/src/lib/env";

import type { Duplex } from "node:stream";

const CONNECT_TIMEOUT_MS = 10_000;

export interface EgressProxyConfig {
  server: URL;
  username: string;
  password: string;
}

export interface ProxyTarget {
  host: string;
  port: number;
}

export class EgressProxyError extends Error {}

function requireEgressProxyEnv(name: string): string {
  try {
    return requireEnv(name);
  } catch {
    throw new EgressProxyError(`${name} environment variable is not set`);
  }
}

export function loadEgressProxyConfig(): EgressProxyConfig {
  const serverValue = requireEgressProxyEnv("EHR_EGRESS_PROXY_SERVER");

  let server: URL;

  try {
    server = new URL(serverValue);
  } catch {
    throw new EgressProxyError("EHR_EGRESS_PROXY_SERVER environment variable must be a valid URL");
  }

  return {
    server,
    username: requireEgressProxyEnv("EHR_EGRESS_PROXY_USERNAME"),
    password: requireEgressProxyEnv("EHR_EGRESS_PROXY_PASSWORD"),
  };
}

export function loadHttpEgressProxyConfig(): EgressProxyConfig {
  const config = loadEgressProxyConfig();

  if (config.server.protocol !== "http:") {
    throw new EgressProxyError("Egress proxy server must use http:");
  }

  return config;
}

export function openEgressProxyTunnel(target: ProxyTarget): Promise<Duplex> {
  const { server, username, password } = loadHttpEgressProxyConfig();

  return new Promise((resolve, reject) => {
    let proxyRequest: ClientRequest | undefined;
    let proxySocket: Duplex | undefined;
    let timeout: NodeJS.Timeout | undefined;
    let settled = false;

    const clearConnectTimeout = (): void => {
      if (timeout) clearTimeout(timeout);
    };

    const fail = (message: string): void => {
      if (settled) return;

      settled = true;
      clearConnectTimeout();
      proxyRequest?.destroy();
      proxySocket?.destroy();
      reject(new EgressProxyError(message));
    };

    const handleRequestError = (): void => {
      fail("Egress proxy CONNECT request failed");
    };

    const targetAuthority = `${target.host}:${target.port}`;

    try {
      proxyRequest = request({
        hostname: server.hostname,
        port: server.port || 80,
        method: "CONNECT",
        path: targetAuthority,
        headers: {
          "Host": targetAuthority,
          "Proxy-Authorization": `Basic ${Buffer.from(`${username}:${password}`).toString("base64")}`,
        },
      });
      proxyRequest.once("error", handleRequestError);
      proxyRequest.once("connect", (response, socket, head) => {
        if (settled) {
          socket.destroy();

          return;
        }

        proxySocket = socket;

        if (response.statusCode !== 200) {
          fail(`Egress proxy CONNECT request returned status ${response.statusCode ?? "unknown"}`);

          return;
        }

        if (head.length > 0) socket.unshift(head);

        settled = true;
        clearConnectTimeout();
        proxyRequest?.removeListener("error", handleRequestError);
        resolve(socket);
      });
      timeout = setTimeout(() => {
        fail("Egress proxy CONNECT request timed out");
      }, CONNECT_TIMEOUT_MS);
      proxyRequest.end();
    } catch {
      fail("Egress proxy CONNECT request failed");
    }
  });
}
